import React, { useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import Cookies from "js-cookie";
import { getQueryParams } from "../interfaces/getQueryParams"; // Ensure the path is correct

const useUtmTracking = () => {
  // Function to check if UTM session is valid
  const isUtmSessionValid = () => {
    const utmSessionUuid = Cookies.get("utm_session_uuid");
    const utmSessionTimestamp = Cookies.get("utm_session_timestamp"); // Assuming you stored timestamp when session was created

    if (!utmSessionUuid || !utmSessionTimestamp) {
      console.warn("No valid UTM session found.");
      return false; // No session or timestamp found, invalid session
    }

    // Check if the session is expired (2 minutes in this example)
    const currentTime = Date.now(); // Get current time in milliseconds
    const sessionAge = currentTime - parseInt(utmSessionTimestamp);

    const sessionTimeout = 2 * 60 * 1000; // 2 minutes in milliseconds

    if (sessionAge > sessionTimeout) {
      console.warn("UTM session has expired.");
      // Optionally, you can remove the session data from cookies
      Cookies.remove("utm_session_uuid");
      Cookies.remove("utm_session_timestamp");
      return false; // Session expired
    }

    console.log("UTM session is valid.");
    return true; // Session is valid
  };

  useEffect(() => {
    let utmSession = Cookies.get("utm_session_uuid"); // Always fetch from cookies for consistency

    // Generate a new session UUID and timestamp if it doesn't exist
    if (!utmSession) {
      utmSession = uuidv4();
      const timestamp = Date.now(); // Get current timestamp in milliseconds

      // Set both localStorage and cookies for session UUID and timestamp
      localStorage.setItem("utm_session_uuid", utmSession);
      localStorage.setItem("utm_session_timestamp", timestamp.toString());

      Cookies.set("utm_session_uuid", utmSession, { expires: 3650 }); // 10 years
      Cookies.set("utm_session_timestamp", timestamp.toString(), { expires: 3650 }); // 10 years
    }

    // Check UTM session validity
    if (!isUtmSessionValid()) {
      console.log("UTM session is NOT valid.");
    } else {
      console.log("UTM session is valid.");
    }

    // Check if UTM data is already stored
    const storedUtm = localStorage.getItem("utm_data");

    if (!storedUtm) {
      const utmParams = getQueryParams();

      // Get current time in BST (Bangladesh Standard Time)
      const now = new Date();
      const options = { timeZone: "Asia/Dhaka", hour12: false };

      // Format the date as YYYY-MM-DD HH:MM:SS
      const formattedTime = new Intl.DateTimeFormat("en-CA", {
        ...options,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
        .format(now)
        .replace(",", "") // Remove any commas
        .replace(/\//g, "-"); // Convert to MySQL-friendly format

      // Store both first-time visit and recent visit timestamp as the same value
      utmParams["first_time_visit"] = formattedTime;
      utmParams["recent_visit"] = formattedTime;

      // Save UTM data in localStorage and cookies
      localStorage.setItem("utm_data", JSON.stringify(utmParams));
      Cookies.set("utm_data", JSON.stringify(utmParams), { expires: 3650 }); // 10 years
    } else {
      // If UTM data is already stored, update recent_visit and save again
      const utmParams = JSON.parse(storedUtm);

      // Get current time in BST (Bangladesh Standard Time)
      const now = new Date();
      const options = { timeZone: "Asia/Dhaka", hour12: false };

      // Format the date as YYYY-MM-DD HH:MM:SS
      const formattedTime = new Intl.DateTimeFormat("en-CA", {
        ...options,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
        .format(now)
        .replace(",", "") // Remove any commas
        .replace(/\//g, "-"); // Convert to MySQL-friendly format

      // Update the recent_visit timestamp (do not change first_time_visit)
      utmParams["recent_visit"] = formattedTime;

      // Save updated UTM data in localStorage and cookies
      localStorage.setItem("utm_data", JSON.stringify(utmParams));
      Cookies.set("utm_data", JSON.stringify(utmParams), { expires: 3650 }); // 10 years
    }
  }, []);
};

export default useUtmTracking;
